import React from 'react';
import serviceName from 'views/EMR/components/ServiceLookup/columns/serviceName';
import code from 'views/EMR/components/ServiceLookup/columns/code';
import modifiers from 'views/EMR/components/ServiceLookup/columns/modifiers';
import linkedICDCodes from 'views/EMR/components/ServiceLookup/columns/linkedICDCodes';
import charge from 'views/EMR/components/ServiceLookup/columns/charge';
import pos from 'views/EMR/components/ServiceLookup/columns/pos';
import patientAuthorization from 'views/EMR/components/ServiceLookup/columns/patientAuthorization';
import serviceDate from 'views/EMR/components/ServiceLookup/columns/serviceDate';
import getUnitColumn from 'views/EMR/components/ServiceLookup/columns/unit';
import {
  ServiceType,
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import icdCode from 'views/EMR/components/ServiceLookup/columns/icdCode';
import pricePerUnit from 'views/EMR/components/ServiceLookup/columns/pricePerUnit';
import drug from 'views/EMR/components/ServiceLookup/columns/drug';
import notes from 'views/EMR/components/ServiceLookup/columns/notes';
import externalUsedQuantity from 'views/EMR/components/ServiceLookup/columns/externalUsedQuantity';
import ApprovedUnitsField from 'views/EMR/components/Authorizations/ApprovedUnitsField';
import isNil from 'lodash/isNil';

const claimHeaders: Readonly<Record<string, {
  title: string; boxNumber?: string;
}>> = Object.freeze({
  code: {
    title: 'Service Codes',
    boxNumber: 'Box 24D',
  },
  modifiers: {
    title: 'Modifiers',
    boxNumber: 'Box 24D',
  },
  linkedICDCodes: {
    title: 'Diagnosis',
    boxNumber: 'Box 24E',
  },
  numberOfUnits: {
    title: 'Units',
    boxNumber: 'Box 24G',
  },
  billedAmount: {
    title: 'Charges',
    boxNumber: 'Box 24F',
  },
  pos: {
    title: 'POS',
    boxNumber: 'Box 24B',
  },
  patientAuthorization: {
    title: 'Prior Auth',
    boxNumber: 'Box 23',
  },
  serviceDate: {
    title: 'Date of Service',
    boxNumber: 'Box 24A',
  },
  drugIdentification: {
    title: 'Drug',
  },
  notes: {
    title: 'Line Note',
  },
});

export const customServiceColumns = [
  { ...serviceName, size: undefined, meta: { autoSized: true } },
  getUnitColumn(1),
  pricePerUnit,
];

export const cptCodesColumns = [
  code,
  modifiers,
  linkedICDCodes,
  getUnitColumn(0),
].map((columnDef: ColumnDef<VisitNoteServiceCode>) => ({
  ...columnDef,
  multiLineHeader: true,
  columnTitle: claimHeaders[columnDef.id ?? '']?.title,
}));

export const authorizationAllowanceCPTCodeColumns = [
  code,
  getUnitColumn(1, {
    label: 'Approved', size: 80, Component: ApprovedUnitsField,
  }),
  externalUsedQuantity,
];

export const icdCodesColumns = [icdCode];

export const claimCPTCodesColumns = [
  ...cptCodesColumns,
  charge,
  pos,
  patientAuthorization,
  serviceDate,
  drug,
  notes,
].map((columnDef: ColumnDef<VisitNoteServiceCode>) => {
  const { title, boxNumber } = claimHeaders[columnDef.id ?? ''] ?? {};
  return ({
    ...columnDef,
    multiLineHeader: true,
    columnTitle: (
      <>
        {title}
        {!isNil(boxNumber) && (
          <>
            <br />
            <span className="text-[10px]">
              (
              {boxNumber}
              )
            </span>
          </>
        )}
      </>
    ),
  });
});

export const columnsByCodeType: Record<
  ServiceType, ColumnDef<VisitNoteServiceCode>[]
> = {
  CPTCode: cptCodesColumns,
  CustomCode: customServiceColumns,
  ICDCode: icdCodesColumns,
};
